import { useMemo } from 'react';
import useMedia from '../useMedia';
import { BREAKPOINT_QUERIES, ORIENTATION_QUERIES } from './const';
import { useDeviceContext } from './context';
import type { UseScreenReturnType } from './types';
import { DeviceVariantType, Orientation } from './types';

export { BREAKPOINT_QUERIES };

/**
 * Use default value from user agent parsed in server, but actual value from media queries
 */

const isTouchEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  return navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
};

export default function useScreen(): UseScreenReturnType {
  const device = useDeviceContext();
  const screenFromMedia = useMedia(
    [BREAKPOINT_QUERIES.desktop, BREAKPOINT_QUERIES.tablet, BREAKPOINT_QUERIES.mobile],
    [DeviceVariantType.DESKTOP, DeviceVariantType.TABLET, DeviceVariantType.MOBILE],
    device || DeviceVariantType.DESKTOP,
  );

  const orientation = useMedia(
    [ORIENTATION_QUERIES.portrait, ORIENTATION_QUERIES.landscape],
    [Orientation.PORTRAIT, Orientation.LANDSCAPE],
    Orientation.PORTRAIT,
  );

  // if desktop size but touch enabled, classify as tablet
  const screen = useMemo(() => {
    // removing this line because one of our users had touch based laptop, so we were incorrectly classifying them as tablet.
    // if (screenFromMedia === DeviceVariantType.DESKTOP && isTouchEnabled()) {
    //   return DeviceVariantType.TABLET;
    // }

    if (screenFromMedia === DeviceVariantType.TABLET && !isTouchEnabled()) {
      return DeviceVariantType.DESKTOP;
    }

    return screenFromMedia;
  }, [screenFromMedia]);

  const isDesktop = screen === DeviceVariantType.DESKTOP;
  const isTablet = screen === DeviceVariantType.TABLET;
  const isMobile = screen === DeviceVariantType.MOBILE;

  return { screen, isDesktop, isTablet, isMobile, orientation };
}
